// hooks/usePinLock.js
import { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { checkCode } from "./checkCode";

// Global state
let globalListeners = new Set();
let isGlobalLocked = false;

const usePinLock = (tryRefreshCallback) => {
  const [isLocked, setIsLocked] = useState(false);
  const isMountedRef = useRef(true);
  const showPinRef = useRef(null);
  const handleCheckRef = useRef(null);

  // Handle PIN verification
  handleCheckRef.current = useCallback(
    async (code) => {
      try {
        return await checkCode(code, tryRefreshCallback);
      } catch (err) {
        console.error("PIN check failed:", err);
        return false;
      }
    },
    [tryRefreshCallback]
  );

  // Update local lock state
  const updateLockState = useCallback((locked) => {
    if (!isMountedRef.current) return;
    setIsLocked(locked);
    isGlobalLocked = locked;
  }, []);

  // Show PIN popup
  showPinRef.current = async () => {
    if (Swal.isVisible()) return;

    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "PIN Is Required To Continue",
        input: "password",
        inputAttributes: {
          maxlength: 4,
          inputmode: "numeric",
          placeholder: "Enter 4-digit PIN",
          autocomplete: "off",
        },
        confirmButtonText: "Verify",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showLoaderOnConfirm: true,
        backdrop: true,
        customClass: {
          popup: "pin-lock-popup",
          input: "pin-lock-input",
        },
        preConfirm: async (inputValue) => {
          if (!inputValue || inputValue.length !== 4 || isNaN(inputValue)) {
            Swal.showValidationMessage("Please enter a valid 4-digit PIN");
            return false;
          }
          const valid = await handleCheckRef.current(parseInt(inputValue, 10));
          if (!valid) {
            Swal.showValidationMessage("Invalid PIN. Try again.");
          }
          return valid;
        },
      });

      if (result.isConfirmed) {
        localStorage.setItem("digital-form-locked", "false");
        document.body.classList.remove("blurred");
        globalListeners.forEach((listener) => listener(false));

        await Swal.fire({
          icon: "success",
          title: "Access Granted",
          text: "PIN verified successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("PIN dialog error:", err);
    }
  };

  // Watch for global lock state changes
  useEffect(() => {
    const handleStorageChange = () => {
      const locked = localStorage.getItem("digital-form-locked") === "true";

      if (locked && !isGlobalLocked) {
        isGlobalLocked = true;
        document.body.classList.add("blurred");
        globalListeners.forEach((listener) => listener(true));
        if (showPinRef.current) showPinRef.current();
      } else if (!locked && isGlobalLocked) {
        isGlobalLocked = false;
        document.body.classList.remove("blurred");
        globalListeners.forEach((listener) => listener(false));
      }
    };

    // Listen to storage changes (works across tabs)
    window.addEventListener("storage", handleStorageChange);

    // Poll localStorage every 1s to ensure detection even if storage event doesn't fire
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Setup component listener
  useEffect(() => {
    isMountedRef.current = true;
    globalListeners.add(updateLockState);

    // Initialize local lock state
    const locked = localStorage.getItem("digital-form-locked") === "true";
    updateLockState(locked);

    // Show PIN popup immediately if locked
    if (locked && showPinRef.current) {
      setTimeout(() => showPinRef.current(), 200);
    }

    return () => {
      isMountedRef.current = false;
      globalListeners.delete(updateLockState);

      // Remove blur if no more listeners
      if (globalListeners.size === 0) document.body.classList.remove("blurred");
    };
  }, [updateLockState]);

  // Manual lock/unlock
  const lockApp = useCallback(() => {
    localStorage.setItem("digital-form-locked", "true");
  }, []);
  const unlockApp = useCallback(() => {
    localStorage.setItem("digital-form-locked", "false");
  }, []);

  return {
    isLocked,
    lockApp,
    unlockApp,
    isGloballyLocked: () => isGlobalLocked,
  };
};

export default usePinLock;
